import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Container,
  Avatar,
  IconButton,
  Chip,
  Card,
  CardContent,
  Fade,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Tab,
  Tabs,
  Button,
  Modal,
  TextField,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import {
  ArrowBack,
  Dashboard,
  Logout,
  Person,
  Email,
  Business,
  Verified,
  Extension,
  Autorenew,
  CalendarToday,
  Key,
  CheckCircle,
  Schedule,
  Add,
  Delete,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { auth, database } from '../firebase/Config';
import { ref, get, child, set, update, remove } from 'firebase/database';

// Estilos para o modal
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
  p: 4,
};

// Função para gerar código de licença
const generateLicenseKey = () => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const segments = [];
  for (let i = 0; i < 4; i++) {
    let segment = '';
    for (let j = 0; j < 5; j++) {
      segment += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    segments.push(segment);
  }
  return segments.join('-');
};

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState('');
  const [userData, setUserData] = useState(null);
  const [userLicenses, setUserLicenses] = useState([]);
  const [userRenewals, setUserRenewals] = useState([]);
  const [plugins, setPlugins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  
  // Estados para modais
  const [openLicenseModal, setOpenLicenseModal] = useState(false);
  const [openRenewalModal, setOpenRenewalModal] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  
  // Estados para nova licença
  const [newLicense, setNewLicense] = useState({
    key: generateLicenseKey(),
    pluginName: '',
    requestCode: '',
    date: new Date().toLocaleDateString(),
  });
  
  // Estados para nova renovação
  const [selectedLicenseForRenewal, setSelectedLicenseForRenewal] = useState('');
  const [renewalData, setRenewalData] = useState({
    expirationDate: '',
  });
  
  // Estados para feedback
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // Estados para validação
  const [errors, setErrors] = useState({});
  
  // Estado para diálogo de exclusão
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    type: '', // 'license' ou 'renewal'
    id: '',
    licenseKey: '',
    name: '',
  });

  const fetchUserData = async () => {
    setLoading(true);
    const dbRef = ref(database);
    
    try {
      // Buscar dados do usuário
      const userSnapshot = await get(child(dbRef, `users/${userId}`));
      if (userSnapshot.exists()) {
        const user = userSnapshot.val();
        setUserData({ id: userId, ...user });

        // Buscar plugins disponíveis
        const pluginsSnapshot = await get(child(dbRef, 'plugins'));
        if (pluginsSnapshot.exists()) {
          const pluginsData = pluginsSnapshot.val();
          const pluginsArray = Object.keys(pluginsData).map((key) => ({
            id: key,
            ...pluginsData[key],
          }));
          setPlugins(pluginsArray);
        }

        // Buscar licenças do usuário
        const licensesSnapshot = await get(child(dbRef, 'licenses'));
        if (licensesSnapshot.exists()) {
          const licensesData = licensesSnapshot.val();
          const userLicensesArray = Object.keys(licensesData)
            .filter(key => licensesData[key].userEmail === user.email)
            .map(key => ({
              id: key,
              ...licensesData[key],
            }));
          setUserLicenses(userLicensesArray);

          // Buscar renovações para cada licença do usuário
          const renewalsSnapshot = await get(child(dbRef, 'renewals'));
          if (renewalsSnapshot.exists()) {
            const renewalsData = renewalsSnapshot.val();
            const userRenewalsArray = [];
            
            userLicensesArray.forEach(license => {
              if (renewalsData[license.id]) {
                Object.keys(renewalsData[license.id]).forEach(renewalId => {
                  userRenewalsArray.push({
                    id: renewalId,
                    licenseKey: license.key,
                    licenseId: license.id,
                    pluginName: license.pluginName,
                    ...renewalsData[license.id][renewalId],
                  });
                });
              }
            });
            setUserRenewals(userRenewalsArray);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao buscar dados do usuário: ', error);
      showSnackbar('Erro ao carregar dados do usuário', 'error');
    }
    
    setLoading(false);
  };

  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      setUserEmail(user.email);
    }
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Funções de feedback
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Funções para licenças
  const handleOpenLicenseModal = () => {
    setNewLicense({
      key: generateLicenseKey(),
      pluginName: '',
      requestCode: '',
      date: new Date().toLocaleDateString(),
    });
    setErrors({});
    setOpenLicenseModal(true);
  };

  const handleCloseLicenseModal = () => {
    setOpenLicenseModal(false);
    setErrors({});
  };

  const validateLicense = () => {
    const newErrors = {};

    if (!newLicense.pluginName) {
      newErrors.pluginName = 'Selecione um plugin';
    } else {
      // Verificar se já existe licença para este plugin
      const existingLicense = userLicenses.find(
        license => license.pluginName === newLicense.pluginName
      );
      if (existingLicense) {
        newErrors.pluginName = 'Usuário já possui licença para este plugin';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddLicense = async () => {
    if (!validateLicense()) {
      showSnackbar('Por favor, corrija os erros no formulário', 'error');
      return;
    }

    setSubmitLoading(true);
    try {
      const licenseId = newLicense.key;

      await set(ref(database, `licenses/${licenseId}`), {
        key: newLicense.key,
        userEmail: userData.email,
        pluginName: newLicense.pluginName,
        requestCode: newLicense.requestCode,
        date: newLicense.date,
        createdBy: userEmail,
      });

      showSnackbar('Licença cadastrada com sucesso!', 'success');
      fetchUserData();
      handleCloseLicenseModal();
    } catch (error) {
      console.error('Erro ao adicionar licença: ', error);
      showSnackbar('Erro ao cadastrar licença', 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleOpenDeleteLicenseDialog = (license) => {
    setDeleteDialog({
      open: true,
      type: 'license',
      id: license.id,
      licenseKey: license.key,
      name: license.pluginName,
    });
  };

  const handleDeleteLicense = async () => {
    try {
      // Excluir a licença
      await remove(ref(database, `licenses/${deleteDialog.id}`));

      // Excluir as renovações associadas
      await remove(ref(database, `renewals/${deleteDialog.id}`));

      showSnackbar('Licença excluída com sucesso!', 'success');
      fetchUserData();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Erro ao excluir licença: ', error);
      showSnackbar('Erro ao excluir licença', 'error');
    }
  };

  // Funções para renovações
  const handleOpenRenewalModal = () => {
    if (userLicenses.length === 0) {
      showSnackbar('Este usuário não possui licenças para renovar', 'warning');
      return;
    }
    setSelectedLicenseForRenewal('');
    setRenewalData({ expirationDate: '' });
    setErrors({});
    setOpenRenewalModal(true);
  };

  const handleCloseRenewalModal = () => {
    setOpenRenewalModal(false);
    setErrors({});
  };

  const validateRenewal = () => {
    const newErrors = {};

    if (!selectedLicenseForRenewal) {
      newErrors.license = 'Selecione uma licença';
    }

    if (!renewalData.expirationDate) {
      newErrors.expirationDate = 'Data de expiração é obrigatória';
    } else {
      const selectedDate = new Date(renewalData.expirationDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate <= today) {
        newErrors.expirationDate = 'Data de expiração deve ser futura';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddRenewal = async () => {
    if (!validateRenewal()) {
      showSnackbar('Por favor, corrija os erros no formulário', 'error');
      return;
    }

    setSubmitLoading(true);
    try {
      const renewalId = `renewal_${new Date().getTime()}`;
      const selectedLicense = userLicenses.find(l => l.id === selectedLicenseForRenewal);

      await set(ref(database, `renewals/${selectedLicenseForRenewal}/${renewalId}`), {
        renewalDate: new Date().toLocaleDateString(),
        expirationDate: renewalData.expirationDate,
        renewedBy: userEmail,
        pluginType: selectedLicense.pluginName,
      });

      await update(ref(database, `licenses/${selectedLicenseForRenewal}`), {
        lastRenewalDate: new Date().toLocaleDateString(),
        expirationDate: renewalData.expirationDate,
        renewedBy: userEmail,
      });

      showSnackbar('Renovação cadastrada com sucesso!', 'success');
      fetchUserData();
      handleCloseRenewalModal();
    } catch (error) {
      console.error('Erro ao adicionar renovação: ', error);
      showSnackbar('Erro ao cadastrar renovação', 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleOpenDeleteRenewalDialog = (renewal) => {
    setDeleteDialog({
      open: true,
      type: 'renewal',
      id: renewal.id,
      licenseKey: renewal.licenseKey,
      licenseId: renewal.licenseId,
      name: renewal.pluginName,
    });
  };

  const handleDeleteRenewal = async () => {
    try {
      await remove(ref(database, `renewals/${deleteDialog.licenseId}/${deleteDialog.id}`));

      showSnackbar('Renovação excluída com sucesso!', 'success');
      fetchUserData();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Erro ao excluir renovação: ', error);
      showSnackbar('Erro ao excluir renovação', 'error');
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, type: '', id: '', licenseKey: '', name: '' });
  };

  const handlePluginChange = (pluginName) => {
    const selectedPlugin = plugins.find(p => p.name === pluginName);
    setNewLicense({
      ...newLicense,
      pluginName: pluginName,
      requestCode: selectedPlugin?.requestCode || '',
    });
    if (errors.pluginName) {
      setErrors({ ...errors, pluginName: null });
    }
  };

  const handleLogout = async () => {
    await auth.signOut();
    navigate('/login');
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!userData) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      }}>
        <Typography variant="h5">Usuário não encontrado</Typography>
      </Box>
    );
  }

  // Agrupar plugins únicos
  const uniquePlugins = [...new Set(userLicenses.map(license => license.pluginName))];

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      {/* Header */}
      <AppBar 
        position="static" 
        elevation={0}
        sx={{ 
          background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <IconButton 
            onClick={() => navigate('/users')}
            sx={{ 
              color: '#fff', 
              mr: 2,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <ArrowBack />
          </IconButton>
          <Dashboard sx={{ mr: 2, fontSize: 32 }} />
          <Typography 
            variant="h5" 
            sx={{ 
              flexGrow: 1, 
              fontWeight: 700,
              letterSpacing: '0.5px',
            }}
          >
            Detalhes do Usuário
          </Typography>
          {userEmail && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip 
                avatar={<Avatar sx={{ bgcolor: '#fff', color: '#667eea' }}>{userEmail.charAt(0).toUpperCase()}</Avatar>}
                label={userEmail}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  fontWeight: 500,
                  px: 1,
                }}
              />
              <IconButton 
                onClick={handleLogout}
                sx={{ 
                  color: '#fff',
                  '&:hover': { 
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Logout />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ mt: 4, pb: 4 }}>
        <Fade in timeout={600}>
          <Box>
            {/* Card de Informações do Usuário */}
            <Card 
              elevation={0}
              sx={{ 
                borderRadius: 4,
                overflow: 'hidden',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                mb: 4,
              }}
            >
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  p: 4,
                  color: '#fff',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Avatar
                    src={userData.photoURL}
                    alt={userData.firstName}
                    sx={{ 
                      width: 120, 
                      height: 120,
                      border: '4px solid #fff',
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                    }}
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                      {userData.firstName} {userData.lastName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Chip 
                        icon={<Email sx={{ color: '#fff !important' }} />}
                        label={userData.email}
                        sx={{ 
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                          color: '#fff',
                          fontWeight: 500,
                        }}
                      />
                      <Chip 
                        icon={<Business sx={{ color: '#fff !important' }} />}
                        label={userData.company}
                        sx={{ 
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                          color: '#fff',
                          fontWeight: 500,
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Estatísticas */}
              <CardContent sx={{ p: 4 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 3, 
                        textAlign: 'center',
                        borderRadius: 3,
                        bgcolor: '#f7fafc',
                        border: '2px solid #e2e8f0',
                      }}
                    >
                      <Extension sx={{ fontSize: 48, color: '#667eea', mb: 1 }} />
                      <Typography variant="h3" sx={{ fontWeight: 700, color: '#2d3748' }}>
                        {uniquePlugins.length}
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#718096' }}>
                        Plugins
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 3, 
                        textAlign: 'center',
                        borderRadius: 3,
                        bgcolor: '#f7fafc',
                        border: '2px solid #e2e8f0',
                      }}
                    >
                      <Verified sx={{ fontSize: 48, color: '#48bb78', mb: 1 }} />
                      <Typography variant="h3" sx={{ fontWeight: 700, color: '#2d3748' }}>
                        {userLicenses.length}
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#718096' }}>
                        Licenças
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 3, 
                        textAlign: 'center',
                        borderRadius: 3,
                        bgcolor: '#f7fafc',
                        border: '2px solid #e2e8f0',
                      }}
                    >
                      <Autorenew sx={{ fontSize: 48, color: '#f6ad55', mb: 1 }} />
                      <Typography variant="h3" sx={{ fontWeight: 700, color: '#2d3748' }}>
                        {userRenewals.length}
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#718096' }}>
                        Renovações
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Tabs de Conteúdo */}
            <Card 
              elevation={0}
              sx={{ 
                borderRadius: 4,
                overflow: 'hidden',
                border: '1px solid rgba(255, 255, 255, 0.3)',
              }}
            >
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={activeTab} 
                  onChange={handleTabChange}
                  sx={{
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                    },
                  }}
                >
                  <Tab icon={<Extension />} iconPosition="start" label="Plugins" />
                  <Tab icon={<Verified />} iconPosition="start" label="Licenças" />
                  <Tab icon={<Autorenew />} iconPosition="start" label="Renovações" />
                </Tabs>
              </Box>

              <CardContent sx={{ p: 4 }}>
                {/* Tab Plugins */}
                {activeTab === 0 && (
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#2d3748' }}>
                      Plugins Ativos
                    </Typography>
                    {uniquePlugins.length > 0 ? (
                      <Grid container spacing={2}>
                        {uniquePlugins.map((plugin, index) => (
                          <Grid item xs={12} sm={6} md={4} key={index}>
                            <Paper 
                              elevation={0}
                              sx={{ 
                                p: 3,
                                borderRadius: 3,
                                border: '2px solid #e2e8f0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                '&:hover': {
                                  borderColor: '#667eea',
                                  transform: 'translateY(-2px)',
                                  transition: 'all 0.3s',
                                },
                              }}
                            >
                              <Extension sx={{ fontSize: 32, color: '#667eea' }} />
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {plugin}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography variant="body1" sx={{ color: '#718096' }}>
                        Nenhum plugin encontrado
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Tab Licenças */}
                {activeTab === 1 && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#2d3748' }}>
                        Licenças Cadastradas
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleOpenLicenseModal}
                        sx={{
                          background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                          color: '#fff',
                          fontWeight: 600,
                          px: 3,
                          py: 1,
                          borderRadius: 2,
                          textTransform: 'none',
                          boxShadow: '0 4px 15px rgba(72, 187, 120, 0.4)',
                          '&:hover': {
                            boxShadow: '0 6px 20px rgba(72, 187, 120, 0.6)',
                            transform: 'translateY(-2px)',
                          },
                          transition: 'all 0.3s',
                        }}
                      >
                        Nova Licença
                      </Button>
                    </Box>
                    {userLicenses.length > 0 ? (
                      <List>
                        {userLicenses.map((license) => (
                          <Paper 
                            key={license.id}
                            elevation={0}
                            sx={{ 
                              mb: 2,
                              borderRadius: 3,
                              border: '2px solid #e2e8f0',
                              overflow: 'hidden',
                            }}
                          >
                            <ListItem
                              secondaryAction={
                                <IconButton 
                                  edge="end" 
                                  onClick={() => handleOpenDeleteLicenseDialog(license)}
                                  sx={{ 
                                    color: '#e53935',
                                    '&:hover': { bgcolor: '#e5393520' }
                                  }}
                                >
                                  <Delete />
                                </IconButton>
                              }
                            >
                              <ListItemIcon>
                                <Verified sx={{ fontSize: 40, color: '#48bb78' }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                      {license.pluginName}
                                    </Typography>
                                    <Chip 
                                      icon={<CheckCircle />}
                                      label="Ativa"
                                      size="small"
                                      sx={{ 
                                        bgcolor: '#48bb7820',
                                        color: '#48bb78',
                                        fontWeight: 600,
                                      }}
                                    />
                                  </Box>
                                }
                                secondary={
                                  <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                      <Key sx={{ fontSize: 16, color: '#718096' }} />
                                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                        {license.key}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <CalendarToday sx={{ fontSize: 16, color: '#718096' }} />
                                      <Typography variant="body2" sx={{ color: '#718096' }}>
                                        Cadastrada em: {license.date}
                                      </Typography>
                                    </Box>
                                    {license.expirationDate && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                        <Schedule sx={{ fontSize: 16, color: '#718096' }} />
                                        <Typography variant="body2" sx={{ color: '#718096' }}>
                                          Expira em: {(() => {
                                            const date = new Date(license.expirationDate + 'T00:00:00');
                                            const day = String(date.getDate()).padStart(2, '0');
                                            const month = String(date.getMonth() + 1).padStart(2, '0');
                                            const year = date.getFullYear();
                                            return `${day}/${month}/${year}`;
                                          })()}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>
                          </Paper>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body1" sx={{ color: '#718096' }}>
                        Nenhuma licença encontrada
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Tab Renovações */}
                {activeTab === 2 && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#2d3748' }}>
                        Histórico de Renovações
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleOpenRenewalModal}
                        sx={{
                          background: 'linear-gradient(135deg, #f6ad55 0%, #ed8936 100%)',
                          color: '#fff',
                          fontWeight: 600,
                          px: 3,
                          py: 1,
                          borderRadius: 2,
                          textTransform: 'none',
                          boxShadow: '0 4px 15px rgba(246, 173, 85, 0.4)',
                          '&:hover': {
                            boxShadow: '0 6px 20px rgba(246, 173, 85, 0.6)',
                            transform: 'translateY(-2px)',
                          },
                          transition: 'all 0.3s',
                        }}
                      >
                        Nova Renovação
                      </Button>
                    </Box>
                    {userRenewals.length > 0 ? (
                      <List>
                        {userRenewals.map((renewal) => (
                          <Paper 
                            key={renewal.id}
                            elevation={0}
                            sx={{ 
                              mb: 2,
                              borderRadius: 3,
                              border: '2px solid #e2e8f0',
                              overflow: 'hidden',
                            }}
                          >
                            <ListItem
                              secondaryAction={
                                <IconButton 
                                  edge="end" 
                                  onClick={() => handleOpenDeleteRenewalDialog(renewal)}
                                  sx={{ 
                                    color: '#e53935',
                                    '&:hover': { bgcolor: '#e5393520' }
                                  }}
                                >
                                  <Delete />
                                </IconButton>
                              }
                            >
                              <ListItemIcon>
                                <Autorenew sx={{ fontSize: 40, color: '#f6ad55' }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    {renewal.pluginName}
                                  </Typography>
                                }
                                secondary={
                                  <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                      <Key sx={{ fontSize: 16, color: '#718096' }} />
                                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                        {renewal.licenseKey}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                      <CalendarToday sx={{ fontSize: 16, color: '#718096' }} />
                                      <Typography variant="body2" sx={{ color: '#718096' }}>
                                        Renovada em: {renewal.renewalDate}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                      <Schedule sx={{ fontSize: 16, color: '#718096' }} />
                                      <Typography variant="body2" sx={{ color: '#718096' }}>
                                        Expira em: {(() => {
                                          const date = new Date(renewal.expirationDate + 'T00:00:00');
                                          const day = String(date.getDate()).padStart(2, '0');
                                          const month = String(date.getMonth() + 1).padStart(2, '0');
                                          const year = date.getFullYear();
                                          return `${day}/${month}/${year}`;
                                        })()}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Person sx={{ fontSize: 16, color: '#718096' }} />
                                      <Typography variant="body2" sx={{ color: '#718096' }}>
                                        Renovado por: {renewal.renewedBy}
                                      </Typography>
                                    </Box>
                                  </Box>
                                }
                              />
                            </ListItem>
                          </Paper>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body1" sx={{ color: '#718096' }}>
                        Nenhuma renovação encontrada
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Modal de Nova Licença */}
            <Modal open={openLicenseModal} onClose={handleCloseLicenseModal}>
              <Fade in={openLicenseModal}>
                <Box sx={modalStyle}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#2d3748', mb: 3 }}>
                    Nova Licença
                  </Typography>

                  <TextField
                    label="Chave de Licença"
                    fullWidth
                    margin="normal"
                    value={newLicense.key}
                    disabled
                    helperText="Gerada automaticamente"
                  />

                  <TextField
                    label="Usuário"
                    fullWidth
                    margin="normal"
                    value={userData?.email || ''}
                    disabled
                  />

                  <FormControl 
                    fullWidth 
                    margin="normal"
                    error={!!errors.pluginName}
                  >
                    <InputLabel>Plugin *</InputLabel>
                    <Select
                      value={newLicense.pluginName}
                      label="Plugin *"
                      onChange={(e) => handlePluginChange(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Selecione um plugin</em>
                      </MenuItem>
                      {plugins
                        .filter(plugin => !userLicenses.some(license => license.pluginName === plugin.name))
                        .map((plugin) => (
                          <MenuItem key={plugin.id} value={plugin.name}>
                            {plugin.name}
                          </MenuItem>
                        ))}
                    </Select>
                    {errors.pluginName && (
                      <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.5 }}>
                        {errors.pluginName}
                      </Typography>
                    )}
                  </FormControl>

                  <TextField
                    label="Código de Requisição"
                    fullWidth
                    margin="normal"
                    value={newLicense.requestCode}
                    disabled
                    helperText="Preenchido automaticamente ao selecionar o plugin"
                  />

                  <TextField
                    label="Data de Cadastro"
                    fullWidth
                    margin="normal"
                    value={newLicense.date}
                    disabled
                  />

                  {userLicenses.length === plugins.length && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Este usuário já possui licenças para todos os plugins disponíveis.
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={handleCloseLicenseModal}
                      sx={{
                        py: 1.5,
                        borderColor: '#718096',
                        color: '#718096',
                        '&:hover': {
                          borderColor: '#2d3748',
                          bgcolor: '#f7fafc',
                        },
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleAddLicense}
                      disabled={submitLoading}
                      sx={{
                        py: 1.5,
                        background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                        color: '#fff',
                        fontWeight: 600,
                        '&:hover': {
                          boxShadow: '0 6px 20px rgba(72, 187, 120, 0.4)',
                        },
                        '&:disabled': {
                          background: '#cbd5e0',
                        },
                      }}
                    >
                      {submitLoading ? (
                        <CircularProgress size={24} sx={{ color: '#fff' }} />
                      ) : (
                        'Cadastrar Licença'
                      )}
                    </Button>
                  </Box>
                </Box>
              </Fade>
            </Modal>

            {/* Modal de Nova Renovação */}
            <Modal open={openRenewalModal} onClose={handleCloseRenewalModal}>
              <Fade in={openRenewalModal}>
                <Box sx={modalStyle}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#2d3748', mb: 3 }}>
                    Nova Renovação
                  </Typography>

                  <FormControl 
                    fullWidth 
                    margin="normal"
                    error={!!errors.license}
                  >
                    <InputLabel>Selecione a Licença *</InputLabel>
                    <Select
                      value={selectedLicenseForRenewal}
                      label="Selecione a Licença *"
                      onChange={(e) => {
                        setSelectedLicenseForRenewal(e.target.value);
                        if (errors.license) setErrors({ ...errors, license: null });
                      }}
                    >
                      <MenuItem value="">
                        <em>Selecione uma licença</em>
                      </MenuItem>
                      {userLicenses.map((license) => (
                        <MenuItem key={license.id} value={license.id}>
                          {license.pluginName} - {license.key}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.license && (
                      <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.5 }}>
                        {errors.license}
                      </Typography>
                    )}
                  </FormControl>

                  <TextField
                    label="Data de Expiração *"
                    fullWidth
                    margin="normal"
                    type="date"
                    value={renewalData.expirationDate}
                    onChange={(e) => {
                      setRenewalData({ ...renewalData, expirationDate: e.target.value });
                      if (errors.expirationDate) setErrors({ ...errors, expirationDate: null });
                    }}
                    error={!!errors.expirationDate}
                    helperText={errors.expirationDate || 'Selecione a data de expiração da licença'}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />

                  <TextField
                    label="Data de Renovação"
                    fullWidth
                    margin="normal"
                    value={new Date().toLocaleDateString()}
                    disabled
                  />

                  <TextField
                    label="Renovado por"
                    fullWidth
                    margin="normal"
                    value={userEmail}
                    disabled
                  />

                  <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={handleCloseRenewalModal}
                      sx={{
                        py: 1.5,
                        borderColor: '#718096',
                        color: '#718096',
                        '&:hover': {
                          borderColor: '#2d3748',
                          bgcolor: '#f7fafc',
                        },
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleAddRenewal}
                      disabled={submitLoading}
                      sx={{
                        py: 1.5,
                        background: 'linear-gradient(135deg, #f6ad55 0%, #ed8936 100%)',
                        color: '#fff',
                        fontWeight: 600,
                        '&:hover': {
                          boxShadow: '0 6px 20px rgba(246, 173, 85, 0.4)',
                        },
                        '&:disabled': {
                          background: '#cbd5e0',
                        },
                      }}
                    >
                      {submitLoading ? (
                        <CircularProgress size={24} sx={{ color: '#fff' }} />
                      ) : (
                        'Cadastrar Renovação'
                      )}
                    </Button>
                  </Box>
                </Box>
              </Fade>
            </Modal>

            {/* Dialog de Confirmação de Exclusão */}
            <Dialog
              open={deleteDialog.open}
              onClose={handleCloseDeleteDialog}
              PaperProps={{
                sx: { borderRadius: 3 }
              }}
            >
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon sx={{ color: '#e53935' }} />
                Confirmar Exclusão
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Tem certeza que deseja excluir {deleteDialog.type === 'license' ? 'a licença' : 'a renovação'} do plugin <strong>{deleteDialog.name}</strong>?
                  {deleteDialog.type === 'license' && ' Todas as renovações associadas também serão excluídas.'}
                  <br /><br />
                  Esta ação não pode ser desfeita.
                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button 
                  onClick={handleCloseDeleteDialog}
                  sx={{ color: '#718096' }}
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={deleteDialog.type === 'license' ? handleDeleteLicense : handleDeleteRenewal}
                  variant="contained"
                  sx={{
                    bgcolor: '#e53935',
                    '&:hover': { bgcolor: '#c62828' }
                  }}
                  startIcon={<Delete />}
                >
                  Excluir
                </Button>
              </DialogActions>
            </Dialog>

            {/* Snackbar para Feedback */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
              <Alert 
                onClose={handleCloseSnackbar} 
                severity={snackbar.severity}
                variant="filled"
                sx={{ width: '100%' }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default UserDetails;
